"use client";

import {JSX, useMemo, useState} from "react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {ButtonBase} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {Spinner} from "@/shared/ui/feedback/spinner";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {type LocationMapData, processSvgMap} from "@/shared/utils/process-svg-map";

import {useSvgLoader} from "./hooks";

import "./style/locations.scss";

export type LocationsProps = {
    title?: string;
    description?: string;
    locations?: Array<LocationItemProps>;
    space?: SpaceProps;
    anchorId?: string;
};

type LocationItemProps = {
    id: string;
    title: string;
    svgMap?: string;
    mapData?: Array<LocationMapData>;
};

export function Locations({title = "Locations", description, locations = [], space, anchorId}: LocationsProps): JSX.Element | null {
    const [activeTab, setActiveTab] = useState<string>(locations[0]?.id || "");

    const activeLocation = locations.find((loc) => loc.id === activeTab);
    const svgPath = activeLocation?.svgMap || "/images/locations/usa-map.svg";

    const {svg: svgSource, isLoading} = useSvgLoader(svgPath);

    const processedSvg = useMemo(() => {
        if (!svgSource) return null;

        if (activeLocation?.mapData && activeLocation.mapData.length > 0) {
            return processSvgMap(svgSource, activeLocation.mapData);
        }

        return svgSource;
    }, [svgSource, activeLocation?.mapData]);

    const sectionVars = useSectionSpace(space);

    if (locations.length === 0) {
        return null;
    }

    return (
        <section className="locations" style={sectionVars} id={anchorId}>
            <ContainerBase>
                {title && (
                    <Typography as="h2" size={{desktop: 24, mobile: 18}} className="locations__title">
                        {title}
                    </Typography>
                )}

                {description && (
                    <Typography as="p" size={{desktop: 14, mobile: 12}} weight={400} className="locations__description">
                        {description}
                    </Typography>
                )}

                <div className="locations__tabs">
                    {locations.map((location) => (
                        <ButtonBase
                            key={location.id}
                            size="xs"
                            variant={activeTab === location.id ? "secondary-outline" : "secondary"}
                            onClick={() => setActiveTab(location.id)}
                        >
                            {location.title}
                        </ButtonBase>
                    ))}
                </div>

                <div className="locations__content">
                    {isLoading ? (
                        <div className="locations__loading">
                            <Spinner size="large" />
                        </div>
                    ) : processedSvg ? (
                        <div className="locations__map-wrapper map-section" dangerouslySetInnerHTML={{__html: processedSvg}} />
                    ) : null}
                </div>
            </ContainerBase>
        </section>
    );
}
